/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-empty-interface */
import { Box, BoxProps, FormLabel, Typography, InputAdornment } from "@mui/material";
import * as MuiIcon from "@mui/icons-material";
import { ChangeEvent, HTMLInputTypeAttribute, ReactNode } from "react";
import { Control } from "react-hook-form/dist/types/form";
import { Path } from "react-hook-form/dist/types/path";
import { FieldValues } from "react-hook-form/dist/types/fields";
import { ControllerRenderProps } from "react-hook-form/dist/types";
import clsx from "clsx";

// components
import FormField, { FormFieldProps } from "./FormField";
import ChargeFormTextFieldController from "./ChargeFormTextFieldController";

type InputBase = Omit<FormFieldProps, "type"> & {
  tooltip?: string;
  gutters?: boolean;
  verify?: boolean;
  fieldType?: HTMLInputTypeAttribute;
  adornment?: {
    position: "start" | "end";
    component: ReactNode;
  };
  containerProps?: BoxProps & {
    withClassName?: string;
  };
  // startAdornment?: ReactNode;
};

interface InputYupType<TFieldValues extends FieldValues = FieldValues>
  extends Omit<InputBase, "onChange"> {
  onChange?: (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field?: ControllerRenderProps<TFieldValues, Path<TFieldValues>>
  ) => void;
  fieldName?: Path<TFieldValues>;
  fieldControl?: Control<TFieldValues, any>;
}

export interface IChargeFormTextField<TFieldValues extends FieldValues = FieldValues>
  extends InputYupType<TFieldValues> {}

const createAdornment = (adornment?: { position: "start" | "end"; component: ReactNode }) => {
  if (adornment) {
    if (adornment.position === "start")
      return {
        InputProps: {
          startAdornment: (
            <InputAdornment position={adornment.position}>{adornment.component}</InputAdornment>
          )
        }
      };
    if (adornment.position === "end")
      return {
        InputProps: {
          endAdornment: (
            <InputAdornment position={adornment.position}>{adornment.component}</InputAdornment>
          )
        }
      };
  }
};

const createPasswordAdornment = (adornment?: {
  position: "start" | "end";
  component: ReactNode;
}) => {
  if (adornment) {
    if (adornment.position === "start")
      return {
        InputProps: {
          startAdornment: (
            <InputAdornment position={adornment.position}>{adornment.component}</InputAdornment>
          )
        }
      };
    if (adornment.position === "end")
      return {
        InputProps: {
          endAdornment: (
            <InputAdornment position={adornment.position}>{adornment.component}</InputAdornment>
          )
        }
      };
  }
};

export default function ChargeFormTextField<TFieldValues extends FieldValues = FieldValues>({
  label,
  tooltip,
  fieldType,
  adornment,
  fieldName,
  fieldControl,
  verify = true,
  containerProps,
  ...props
}: IChargeFormTextField<TFieldValues>) {
  const { withClassName, ..._containerProps } = containerProps ?? {};
  const formTextProps = {
    ...props,
    ...(fieldType === "password"
      ? createPasswordAdornment(adornment)
      : createAdornment(adornment))
  };

  return (
    <Box
      className={clsx("flex flex-col", withClassName, {
        "w-full": props.fullWidth
      })}
      {..._containerProps}
    >
      <FormLabel
        htmlFor={props.id}
        className={clsx({
          hidden: !label
        })}
      >
        <Typography
          variant="body2"
          className="ml-2"
          color={props?.error ? "error" : "text.primary"}
        >
          {label}
          {tooltip && (
            <MuiIcon.InfoOutlined sx={{ width: 20, height: 20 }} className="ml-2 -mt-1" />
          )}
        </Typography>
      </FormLabel>
      {fieldControl && fieldName ? (
        <ChargeFormTextFieldController
          verify={verify}
          fieldName={fieldName}
          fieldType={fieldType}
          adornment={adornment}
          fieldControl={fieldControl}
          {...props}
        />
      ) : (
        <FormField type={fieldType} {...formTextProps} />
      )}
    </Box>
  );
}
